import { TabelaProcedimentoList } from "@/modules/ADM/TabelaProcedimento";
import { useClinic } from "@/components/Context/ClinicaContext";
import { useCEP } from "@/hooks/useCEP";
import { useConsultaCNPJ } from '@/hooks/useValidaCNPJ';
import { validateCNPJ } from '@/utils/pixValidator';
import { convenioSchema } from '@/validations';
import { AddIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  useDisclosure
} from "@chakra-ui/react";
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { IMaskInput } from "react-imask";
import SelectReact from "react-select";
import { useTabelasProcedimentos } from "../hooks/useConvenio";


const ConvenioForm = ({ initialData, onSubmit, formId }) => {
  const { data: tabelasProcedimento = [], refetch: refetchTabelas } = useTabelasProcedimentos();
  const { selectedClinic } = useClinic()

  const {
    isOpen: isTabelaModalOpen,
    onOpen: onTabelaModalOpen,
    onClose: onTabelaModalClose,
  } = useDisclosure();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: zodResolver(convenioSchema),
    values: {
      nomeConvenio: initialData?.nomeConvenio || "",
      tipoConvenio: initialData?.tipoConvenio || "",
      registroANS: initialData?.registroANS || "",
      cnpj: initialData?.cnpj || "",
      cnes: initialData?.cnes || "",
      email: initialData?.email || "",
      telefone: initialData?.telefone || "",
      celular: initialData?.celular || "",
      responsavelCadastro: initialData?.responsavelCadastro || "",
      tabelaProcedimento: initialData?.tabelaProcedimento || [],
      periodoRetorno: initialData?.periodoRetorno || "",
      tempoParaReceber: initialData?.tempoParaReceber || "",
      diaRecebimento: initialData?.diaRecebimento || "",
      endereco: initialData?.endereco || "",
      numero: initialData?.numero || "",
      cep: initialData?.cep || "",
      complemento: initialData?.complemento || "",
      bairro: initialData?.bairro || "",
      cidade: initialData?.cidade || "",
      estado: initialData?.estado || "",
      pais: initialData?.pais || "",
      clinica: initialData?.clinica || selectedClinic?._id,
      isValidCNPJ: !!initialData?.cnpj,
    },
  });

  const cnpjValue = watch('cnpj');
  const cnpjCompleto = cnpjValue?.replace(/\D/g, '').length === 14;
  const cnpjValido = cnpjCompleto && validateCNPJ(cnpjValue);

  const { consultarCNPJ, isLoading: isLoadingCNPJ } = useConsultaCNPJ((data) => {
    Object.entries(data).forEach(([key, value]) => setValue(key, value, { shouldValidate: true }));
  });


  const { handleCEPBlur } = useCEP((data) => {
    Object.entries(data).forEach(([key, value]) => setValue(key, value));
  });


  const tabelasParaConvenio = useMemo(
    () =>
      tabelasProcedimento.filter((t) => {
        const tipo = t.tipoTabelaProcedimento || "ambos";
        return tipo !== "particular";
      }),
    [tabelasProcedimento],
  );

  return (
    <>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4} minW="0">
          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0" isRequired isInvalid={!!errors.nomeConvenio}>
              <FormLabel>Nome do convênio</FormLabel>
              <Input {...register('nomeConvenio')} />
              <FormErrorMessage>{errors.nomeConvenio?.message}</FormErrorMessage>
            </FormControl>
            <FormControl minW="0" isRequired isInvalid={!!errors.tipoConvenio}>
              <FormLabel>Tipo do convênio</FormLabel>
              <Input {...register('tipoConvenio')} />
              <FormErrorMessage>{errors.tipoConvenio?.message}</FormErrorMessage>
            </FormControl>
          </Flex>
          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0" isRequired isInvalid={!!errors.registroANS}>
              <FormLabel>Registro ANS</FormLabel>
              <Input {...register('registroANS')} />
              <FormErrorMessage>{errors.registroANS?.message}</FormErrorMessage>
            </FormControl>
            <FormControl minW="0" isRequired isInvalid={!!errors.cnpj || !!errors.isValidCNPJ}>
              <FormLabel>CNPJ</FormLabel>
              <Controller
                name="cnpj"
                control={control}
                render={({ field }) => (
                  <InputGroup>
                    <Input
                      as={IMaskInput}
                      mask="00.000.000/0000-00"
                      value={field.value}
                      onAccept={(val) => field.onChange(val)}
                      placeholder="__.___.___/____-__"
                      pr="2.5rem"
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label="Consultar CNPJ"
                        icon={<SearchIcon />}
                        size="sm"
                        colorScheme="teal"
                        isDisabled={!cnpjValido || !!initialData?.cnpj}
                        isLoading={isLoadingCNPJ}
                        onClick={() => consultarCNPJ(cnpjValue)}
                      />
                    </InputRightElement>
                  </InputGroup>
                )}
              />
              <FormErrorMessage>{errors.cnpj?.message || errors.isValidCNPJ?.message}</FormErrorMessage>
            </FormControl>
          </Flex>

          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0">
              <FormLabel>CNES</FormLabel>
              <Input

                {...register('cnes')}
              />
            </FormControl>
            <FormControl minW="0" isRequired isInvalid={!!errors.email}>
              <FormLabel>E-mail</FormLabel>
              <Input {...register('email')} />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
            <FormControl minW="0">
              <FormLabel>Telefone</FormLabel>
              <Controller
                name="telefone"
                control={control}
                render={({ field }) => (
                  <Input
                    as={IMaskInput}
                    mask="(00) 0000-0000"
                    value={field.value}
                    onAccept={(val) => field.onChange(val)}
                    placeholder="(__) ____-____"
                  />
                )}
              />
            </FormControl>
          </Flex>

          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0">
              <FormLabel>Celular/WhatsApp</FormLabel>
              <Controller
                name="celularWhatsApp"
                control={control}
                render={({ field }) => (
                  <Input
                    as={IMaskInput}
                    mask="(00) 00000-0000"
                    value={field.value}
                    onAccept={(val) => field.onChange(val)}
                    placeholder="(__) _____-____"
                  />
                )}
              />
            </FormControl>

            <FormControl minW="0" isRequired isInvalid={!!errors.responsavelCadastro}>
              <FormLabel>Responsável do cadastro</FormLabel>
              <Input {...register('responsavelCadastro')} />
              <FormErrorMessage>{errors.responsavelCadastro?.message}</FormErrorMessage>
            </FormControl>
          </Flex>

          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0" isRequired isInvalid={!!errors.tabelaProcedimento}>
              <Flex align="center" justify="space-between" gap={2} mb={2}>
                <FormLabel mb="0">Tabela de Procedimento</FormLabel>
                <Button
                  size="xs"
                  colorScheme="teal"
                  p={1}
                  minW="44px" minH="44px" flexShrink={0}
                  onClick={onTabelaModalOpen}
                  aria-label="Cadastrar tabela de procedimentos"
                >
                  <AddIcon boxSize={3} />
                </Button>
              </Flex>
              <Controller
                name="tabelaProcedimento"
                control={control}
                render={({ field }) => (
                  <SelectReact
                    menuPlacement="auto"
                    styles={{
                      container: (base) => ({ ...base, minWidth: 0 }),
                      option: (base) => ({ ...base, overflowWrap: "anywhere" }),
                      multiValue: (base) => ({ ...base, maxWidth: "100%" }),
                      multiValueLabel: (base) => ({ ...base, whiteSpace: "normal", overflowWrap: "anywhere" }),
                    }}
                    placeholder="Digite para filtrar e selecione"
                    isSearchable
                    isMulti
                    options={tabelasParaConvenio.map((tabela) => ({
                      value: tabela.nome,
                      label: tabela.nome,
                    }))}
                    value={(Array.isArray(field.value) ? field.value : field.value ? [field.value] : []).map(
                      (nomeTabela) => ({ value: nomeTabela, label: nomeTabela })
                    )}
                    onChange={(opt) => field.onChange((opt || []).map((o) => o.value))}
                  />
                )}
              />
              <FormErrorMessage>{errors.tabelaProcedimento?.message}</FormErrorMessage>
            </FormControl>

            <FormControl minW="0" isInvalid={!!errors.periodoRetorno}>
              <FormLabel>Período de retorno</FormLabel>
              <InputGroup>
                <NumberInput min={1} w="full" minW="0">
                  <NumberInputField {...register('periodoRetorno')} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <InputRightAddon>Dias</InputRightAddon>
              </InputGroup>
              <FormErrorMessage>{errors.periodoRetorno?.message}</FormErrorMessage>
            </FormControl>
          </Flex>

          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0" isInvalid={!!errors.tempoParaReceber}>
              <FormLabel>Tempo até receber</FormLabel>
              <InputGroup>
                <NumberInput min={1} w="full" minW="0">
                  <NumberInputField {...register('tempoParaReceber')} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <InputRightAddon>Dias</InputRightAddon>
              </InputGroup>
              <FormErrorMessage>{errors.tempoParaReceber?.message}</FormErrorMessage>
            </FormControl>

            <FormControl minW="0" isInvalid={!!errors.diaRecebimento}>
              <FormLabel>Dia do recebimento</FormLabel>
              <InputGroup>
                <NumberInput min={1} w="full" minW="0">
                  <NumberInputField {...register('diaRecebimento')} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <InputRightAddon>Dias</InputRightAddon>
              </InputGroup>
              <FormErrorMessage>{errors.diaRecebimento?.message}</FormErrorMessage>
            </FormControl>
          </Flex>

          <Heading color="teal.500" size="md" pt={2}>
            Endereço
          </Heading>
          <Divider />

          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0" isRequired isInvalid={!!errors.cep}>
              <FormLabel>CEP</FormLabel>
              <Controller
                name="cep"
                control={control}
                render={({ field }) => (
                  <Input
                    as={IMaskInput}
                    mask="00000-000"
                    value={field.value}
                    onAccept={(val) => field.onChange(val)}
                    onBlur={handleCEPBlur}
                    placeholder="_____-___"
                  />
                )}
              />
              <FormErrorMessage>{errors.cep?.message}</FormErrorMessage>
            </FormControl>
            <FormControl minW="0">
              <FormLabel>Endereço</FormLabel>
              <Input {...register('endereco')} />
            </FormControl>
            <FormControl minW="0">
              <FormLabel>Número</FormLabel>
              <Input {...register('numero')} />
            </FormControl>
          </Flex>
          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0" flex={1}>
              <FormLabel>Complemento</FormLabel>
              <Input {...register('complemento')} />
            </FormControl>
            <FormControl minW="0" flex={1}>
              <FormLabel>Estado</FormLabel>
              <Input {...register('estado')} />
            </FormControl>
            <FormControl minW="0" flex={1}>
              <FormLabel>País</FormLabel>
              <Input {...register('pais')} />
            </FormControl>
          </Flex>
          <Flex direction={{ base: "column", md: "row" }} gap={4} minW="0">
            <FormControl minW="0" flex={1}>
              <FormLabel>Bairro</FormLabel>
              <Input {...register('bairro')} />
            </FormControl>
            <FormControl minW="0" flex={1}>
              <FormLabel>Cidade</FormLabel>
              <Input {...register('cidade')} />
            </FormControl>
          </Flex>
        </Stack>
      </form>

      <Modal
        isOpen={isTabelaModalOpen}
        onClose={() => {
          onTabelaModalClose();
          refetchTabelas();
        }}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent w={{ base: "calc(100vw - 16px)", md: "calc(100vw - 48px)" }} maxW="6xl" my={{ base: 2, md: 6 }} maxH={{ base: "calc(100dvh - 16px)", md: "calc(100dvh - 48px)" }}>
          <ModalHeader pr={12} overflowWrap="anywhere">Tabela de procedimentos</ModalHeader>
          <ModalCloseButton />
          <ModalBody px={{ base: 3, md: 6 }} minH="0">
            <TabelaProcedimentoList tipo="convenio" />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ConvenioForm;
